import { useEffect } from "react";

const SITE = {
  name: "Azuhomes",
  origin: "https://azuhomes.com.br",
  defaultTitle: "Azuhomes · Casas de férias em Pernambuco",
  defaultDescription:
    "Casas selecionadas no litoral e na serra de Pernambuco. Reserva direta, atendimento personalizado e uma jornada segura do pagamento ao check-out.",
};

function upsertMeta({ name, property, content }) {
  if (!content) return;
  const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement("meta");
    if (name) tag.setAttribute("name", name);
    if (property) tag.setAttribute("property", property);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function upsertLink({ rel, href }) {
  if (!href) return;
  let tag = document.head.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
}

function upsertJsonLd(id, payload) {
  // Remove any prior JSON-LD block with the same id (avoids duplicate schemas per navigation).
  document.querySelectorAll(`script[type="application/ld+json"][data-id="${id}"]`)
    .forEach((n) => n.remove());
  if (!payload) return;
  const s = document.createElement("script");
  s.type = "application/ld+json";
  s.dataset.id = id;
  s.textContent = JSON.stringify(payload);
  document.head.appendChild(s);
}

/**
 * usePageSeo — hook minimalista para SEO por página em SPA.
 * Sem dependências novas. Atualiza title/description/canonical/OG/Twitter/JSON-LD via useEffect.
 *
 * @param {{
 *   title?: string,        // sem prefixo — a função já concatena "· Azuhomes"
 *   description?: string,
 *   path?: string,         // ex.: "/casas"  (usa location.pathname se ausente)
 *   image?: string,        // url absoluta da imagem OG
 *   type?: "website" | "article" | "product",
 *   noindex?: boolean,
 *   jsonLd?: object | object[] | null,
 * }} opts
 */
export default function usePageSeo(opts = {}) {
  useEffect(() => {
    const title = opts.title
      ? `${opts.title} · ${SITE.name}`
      : SITE.defaultTitle;
    const description = opts.description || SITE.defaultDescription;
    const path = opts.path || (typeof window !== "undefined" ? window.location.pathname : "/");
    const canonical = `${SITE.origin}${path === "/" ? "/" : path.replace(/\/+$/, "")}`;
    const type = opts.type || "website";
    const image = opts.image; // optional (may be undefined; then OG image tag is skipped)

    if (typeof document !== "undefined") {
      document.title = title;

      upsertMeta({ name: "description", content: description });
      upsertMeta({ name: "robots", content: opts.noindex ? "noindex,nofollow" : "index, follow" });

      // Open Graph
      upsertMeta({ property: "og:type", content: type });
      upsertMeta({ property: "og:title", content: title });
      upsertMeta({ property: "og:description", content: description });
      upsertMeta({ property: "og:url", content: canonical });
      upsertMeta({ property: "og:site_name", content: SITE.name });
      upsertMeta({ property: "og:locale", content: "pt_BR" });
      if (image) upsertMeta({ property: "og:image", content: image });

      // Twitter
      upsertMeta({ name: "twitter:card", content: image ? "summary_large_image" : "summary" });
      upsertMeta({ name: "twitter:title", content: title });
      upsertMeta({ name: "twitter:description", content: description });
      if (image) upsertMeta({ name: "twitter:image", content: image });

      // Canonical
      upsertLink({ rel: "canonical", href: canonical });

      // JSON-LD (uma ou várias entidades)
      const blocks = Array.isArray(opts.jsonLd)
        ? opts.jsonLd
        : opts.jsonLd
          ? [opts.jsonLd]
          : [];
      blocks.forEach((b, i) => upsertJsonLd(`page-jsonld-${i}`, b));
      // Limpar blocos anteriores acima do total atual (evita "vazamento" entre páginas)
      for (let i = blocks.length; i < 6; i++) upsertJsonLd(`page-jsonld-${i}`, null);
    }
  }, [
    opts.title, opts.description, opts.path, opts.image,
    opts.type, opts.noindex, JSON.stringify(opts.jsonLd || null),
  ]);
}

// JSON-LD helpers — organização + LodgingBusiness base para a Home
export const organizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE.name,
  url: SITE.origin,
  sameAs: [],
});

export const lodgingBusinessSchema = ({ email, phone, city } = {}) => ({
  "@context": "https://schema.org",
  "@type": "LodgingBusiness",
  name: SITE.name,
  url: SITE.origin,
  description: SITE.defaultDescription,
  areaServed: [
    { "@type": "Place", name: "Praia dos Carneiros, Pernambuco" },
    { "@type": "Place", name: "Tamandaré, Pernambuco" },
    { "@type": "Place", name: "Gravatá, Pernambuco" },
    { "@type": "Place", name: "Recife, Pernambuco" },
  ],
  ...(email ? { email } : {}),
  ...(phone ? { telephone: phone } : {}),
  ...(city ? { address: { "@type": "PostalAddress", addressLocality: city, addressRegion: "PE", addressCountry: "BR" } } : {}),
});

export const vacationRentalSchema = (house) => ({
  "@context": "https://schema.org",
  "@type": "VacationRental",
  name: house?.name,
  description: house?.description || house?.tagline,
  image: (house?.images || []).slice(0, 6),
  numberOfBedrooms: house?.bedrooms,
  numberOfBathroomsTotal: house?.bathrooms,
  occupancy: house?.max_guests ? {
    "@type": "QuantitativeValue",
    value: house.max_guests,
  } : undefined,
  address: house?.city ? {
    "@type": "PostalAddress",
    addressLocality: house.city,
    addressRegion: "PE",
    addressCountry: "BR",
  } : undefined,
  offers: house?.base_price ? {
    "@type": "Offer",
    price: String(house.base_price),
    priceCurrency: "BRL",
    availability: "https://schema.org/InStock",
  } : undefined,
});
